import type { SlotComposite } from 'types/api-responses';
import { CrossPlotAxis } from './type-curve-filters/utils';

export interface CrossPlotChartConfig {
  id: string;
  title: string;
  xAxis: CrossPlotAxis;
  yAxis: CrossPlotAxis;
  visible: boolean;
}

export interface ChartSeriesVisibility {
  oil: boolean;
  gas: boolean;
  water: boolean;
}

export interface RatiosChartVisibility {
  gor: boolean;
  wor: boolean;
  wgr: boolean;
  cgr: boolean;
  watercut: boolean;
}

/**
 * Types for the type-curve wells query POST /queries/wells
 */

// --- Request ---

export interface TcWellsQueryPayload {
  page: number;
  pageSize: number;
  limit: number;
  sortAttribute?: string;
  sortDirection?: string;
  includeTypeCurveFormation: boolean;
  useElastic?: boolean;

  // Type curve context
  tcMode?: boolean;
  typeCurveDateCreated?: string;
  dcaScenarioId?: number;
  tcLateralLengthFt?: number;
  useTcScenarioScaling?: boolean;
  typeCurveHeaderDetailsId?: number;
  wellQualifierIds?: string[];

  // Spatial filters
  polygonEwkb?: string;
  polygonGeojson?: {
    coordinates: number[][][] | number[][][][];
    type: string;
  };
  bounding_box?: {
    sw_lng: number;
    sw_lat: number;
    ne_lng: number;
    ne_lat: number;
  };
  /**
   * Match the shape filters against the perforated lateral
   * (`estimated_producing_segment`) instead of the whole directional survey
   * (`geometry` = survey || stick). Inventory sets this: a well matters only
   * where it drains, so a pad inside the shape whose lateral runs out of it is
   * not one of its wells. Absent means the survey predicate, which is what the
   * Type Curve pages and the promote-time analog resolver want.
   */
  spatialMatchOnLateral?: boolean;
  /**
   * Let a wellbore with no perforated lateral match on its survey instead of
   * being dropped. Only meaningful alongside `spatialMatchOnLateral`. Not every
   * wellbore has an estimated lateral — some permits do, some don't — and a
   * lateral-only predicate loses the ones that don't. Verticals also have no
   * lateral; the hole-direction filter is what keeps those out, not this.
   */
  spatialLateralSurveyFallback?: boolean;
  /**
   * A hard AND ceiling on top of whatever the spatial filters let through: no
   * well outside these units. Inventory sends the selected zone's unit
   * footprint here when the operator has narrowed the map with a drawn AOI or
   * the visible extent — those are exploration shapes unrelated to the units,
   * so on their own they reach well past the inventory.
   */
  inventoryUnitScopeGeojson?: {
    coordinates: number[][][] | number[][][][];
    type: string;
  };

  // Well identity
  analogWellUwiIds?: string[];
  text?: string;

  // Categorical filters
  holeDirectionIds?: string[];
  businessIds?: string[];
  statusIds?: string[];
  stateAbbreviationIds?: string[];
  currentOperatorIds?: string[];
  srpLandingZones?: string[];
  srpLandingZoneIds?: string[];
  nameIds?: string[];
  leaseNameIds?: string[];
  wellNumberIds?: string[];
  uwiIds?: string[];
  excludeUwis?: string[];
  proppantMeshSizeIds?: string[];
  fluidSystemIds?: string[];
  typeCurveFormationIds?: string[];
  structureGridCollectionId?: number | null;
  landingZoneIds?: string[];
  formationIds?: string[];
  parentChildStatusIds?: string[];

  // Date range filters
  spudDate?: { start?: string; end?: string };
  permitDate?: { start?: string; end?: string };
  completionDate?: { start?: string; end?: string };
  rigReleaseDate?: { start?: string; end?: string };
  firstProductionDate?: { start?: string; end?: string };
  jobDate?: { start?: string; end?: string };
  createdAt?: { start?: string; end?: string };
  updatedAt?: { start?: string; end?: string };

  // Numeric range filters
  measuredDepth?: { min?: number; max?: number };
  trueVerticalDepth?: { min?: number; max?: number };
  perforatedLateralLengthFt?: { min?: number; max?: number };
  proppantLbsPerFt?: { min?: number; max?: number };
  fluidGalPerFt?: { min?: number; max?: number };
  fluidBblPerFt?: { min?: number; max?: number };
  waterGalPerFt?: { min?: number; max?: number };
  waterBblPerFt?: { min?: number; max?: number };
  acidGalPerFt?: { min?: number; max?: number };
  acidBblPerFt?: { min?: number; max?: number };
  stageSpacingFt?: { min?: number; max?: number };

  // EUR / ratio range filters
  oilEurBbl?: { min?: number; max?: number };
  gasEurMcf?: { min?: number; max?: number };
  waterEurBbl?: { min?: number; max?: number };
  oilEurPerFtBblFt?: { min?: number; max?: number };
  gasEurPerFtMcfFt?: { min?: number; max?: number };
  waterEurPerFtBblFt?: { min?: number; max?: number };
  gorMcfBbl?: { min?: number; max?: number };
  worBblBbl?: { min?: number; max?: number };
  cgrBblMmcf?: { min?: number; max?: number };
  wgrBblMmcf?: { min?: number; max?: number };
  watercutPct?: { min?: number; max?: number };
  scaledOilEurBbl?: { min?: number; max?: number };
  scaledGasEurMcf?: { min?: number; max?: number };
  scaledWaterEurBbl?: { min?: number; max?: number };

  // Landing Analysis range filters
  distanceToTopFt?: { min?: number; max?: number };

  // Well Spacing range filters
  closestInZoneTrueDistanceFt?: { min?: number; max?: number };
  closestInZoneHzDistanceFt?: { min?: number; max?: number };
  closestInZoneVtDistanceFt?: { min?: number; max?: number };
  closestInZoneOverlapPct?: { min?: number; max?: number };
  secondClosestInZoneTrueDistanceFt?: { min?: number; max?: number };
  secondClosestInZoneHzDistanceFt?: { min?: number; max?: number };
  secondClosestInZoneVtDistanceFt?: { min?: number; max?: number };
  secondClosestInZoneOverlapPct?: { min?: number; max?: number };
  avgInZoneTrueDistanceFt?: { min?: number; max?: number };
  avgInZoneHzDistanceFt?: { min?: number; max?: number };
  avgInZoneVtDistanceFt?: { min?: number; max?: number };
  closestAnyZoneTrueDistanceFt?: { min?: number; max?: number };
  closestAnyZoneHzDistanceFt?: { min?: number; max?: number };
  closestAnyZoneVtDistanceFt?: { min?: number; max?: number };
  closestAnyZoneOverlapPct?: { min?: number; max?: number };
  secondClosestAnyZoneTrueDistanceFt?: { min?: number; max?: number };
  secondClosestAnyZoneHzDistanceFt?: { min?: number; max?: number };
  secondClosestAnyZoneVtDistanceFt?: { min?: number; max?: number };
  secondClosestAnyZoneOverlapPct?: { min?: number; max?: number };
  avgAnyZoneTrueDistanceFt?: { min?: number; max?: number };
  avgAnyZoneHzDistanceFt?: { min?: number; max?: number };
  avgAnyZoneVtDistanceFt?: { min?: number; max?: number };
  numberOfParents?: { min?: number; max?: number };
  numberOfChildren?: { min?: number; max?: number };
  numberOfSiblings?: { min?: number; max?: number };
}

// --- Response ---

export interface CategoricalFacetBucket {
  id: string;
  name: string;
  count: number;
}

export interface CategoricalFacet {
  buckets: CategoricalFacetBucket[];
}

export interface RangeFacet {
  min: number | string | null;
  max: number | string | null;
}

export interface TcWellsFacets {
  // Categorical
  holeDirection?: CategoricalFacet;
  status?: CategoricalFacet;
  state?: CategoricalFacet;
  currentOperator?: CategoricalFacet;
  wellName?: CategoricalFacet;
  leaseName?: CategoricalFacet;
  srpLandingZone?: CategoricalFacet;
  uwi?: CategoricalFacet;
  wellNumber?: CategoricalFacet;
  typeCurveFormation?: CategoricalFacet;
  wellQualifier?: CategoricalFacet;
  landingZone?: CategoricalFacet;
  formation?: CategoricalFacet;
  parentChildStatus?: CategoricalFacet;
  businessId?: CategoricalFacet;
  proppantMeshSize?: CategoricalFacet;
  fluidSystem?: CategoricalFacet;

  // Date ranges
  permitDate?: RangeFacet;
  spudDate?: RangeFacet;
  completionDate?: RangeFacet;
  firstProductionDate?: RangeFacet;
  jobDate?: RangeFacet;

  // Numeric ranges
  trueVerticalDepthFeet?: RangeFacet;
  perforatedLateralLengthFt?: RangeFacet;
  scaledOilEurBbl?: RangeFacet;
  scaledGasEurMcf?: RangeFacet;
  scaledWaterEurBbl?: RangeFacet;
  oilEurBbl?: RangeFacet;
  gasEurMcf?: RangeFacet;
  waterEurBbl?: RangeFacet;
  oilEurPerFtBblFt?: RangeFacet;
  gasEurPerFtMcfFt?: RangeFacet;
  waterEurPerFtBblFt?: RangeFacet;
  gorMcfBbl?: RangeFacet;
  worBblBbl?: RangeFacet;
  cgrBblMmcf?: RangeFacet;
  wgrBblMmcf?: RangeFacet;
  watercutPct?: RangeFacet;
  proppantLbsPerFt?: RangeFacet;
  fluidGalPerFt?: RangeFacet;
  fluidBblPerFt?: RangeFacet;
  waterGalPerFt?: RangeFacet;
  waterBblPerFt?: RangeFacet;
  acidGalPerFt?: RangeFacet;
  acidBblPerFt?: RangeFacet;
  stageSpacingFt?: RangeFacet;

  // Landing Analysis facets
  distanceToTopFt?: RangeFacet;

  // Well Spacing facets
  closestInZoneTrueDistanceFt?: RangeFacet;
  closestInZoneHzDistanceFt?: RangeFacet;
  closestInZoneVtDistanceFt?: RangeFacet;
  closestInZoneOverlapPct?: RangeFacet;
  secondClosestInZoneTrueDistanceFt?: RangeFacet;
  secondClosestInZoneHzDistanceFt?: RangeFacet;
  secondClosestInZoneVtDistanceFt?: RangeFacet;
  secondClosestInZoneOverlapPct?: RangeFacet;
  avgInZoneTrueDistanceFt?: RangeFacet;
  avgInZoneHzDistanceFt?: RangeFacet;
  avgInZoneVtDistanceFt?: RangeFacet;
  closestAnyZoneTrueDistanceFt?: RangeFacet;
  closestAnyZoneHzDistanceFt?: RangeFacet;
  closestAnyZoneVtDistanceFt?: RangeFacet;
  closestAnyZoneOverlapPct?: RangeFacet;
  secondClosestAnyZoneTrueDistanceFt?: RangeFacet;
  secondClosestAnyZoneHzDistanceFt?: RangeFacet;
  secondClosestAnyZoneVtDistanceFt?: RangeFacet;
  secondClosestAnyZoneOverlapPct?: RangeFacet;
  avgAnyZoneTrueDistanceFt?: RangeFacet;
  avgAnyZoneHzDistanceFt?: RangeFacet;
  avgAnyZoneVtDistanceFt?: RangeFacet;
  numberOfParents?: RangeFacet;
  numberOfChildren?: RangeFacet;
  numberOfSiblings?: RangeFacet;

  [key: string]: CategoricalFacet | RangeFacet | undefined;
}

// Shape of the `facets` envelope returned by POST /queries/development_areas.
// Per the spec each picklist facet now wraps its options in a `buckets` array
// (matching the wells endpoint), and range facets stay as `{ min, max }`.
// `DevelopmentAreaFacets` is the canonical type; this is a re-export so the
// existing type-curve imports keep working.
export type TcUnitsFacets = import('types/api-responses').DevelopmentAreaFacets;

export type TcUnitFilters = Record<
  string,
  string[] | string | number | boolean | undefined
>;

// Minimal well context returned alongside the filtered UWI list. Carried at
// this response level so downstream views (probit tooltips, etc.) can show
// well name + qualifier without a second fetch against the wells table.
export interface FilteredWell {
  uwi: string;
  name: string;
  wellQualifier: string;
}

export interface TcWellsQueryResponse {
  results: any[];
  totalCount: number;
  page: number;
  pageSize: number;
  filteredUwis?: FilteredWell[];
  boundingBox?: {
    points: [number, number][];
  };
  facets?: TcWellsFacets;
  statusCounts?: TcWellsStatusCounts;
}

/**
 * Per-status well counts for the SAME set `totalCount` counts — every active
 * filter applied. Not the `status` facet, which self-excludes the status
 * filter so the sidebar stays multi-selectable and therefore does not sum to
 * the total. `total` is the backend's own count of the filtered set, so a
 * status the backend has no bucket for still shows up in the difference rather
 * than silently vanishing.
 */
export interface TcWellsStatusCounts {
  total: number;
  values: { id: string; name: string; count: number }[];
  /**
   * The formation filter these counts were produced under, or null when none
   * was applied. Set client-side from the payload that was actually sent.
   *
   * Selecting a zone changes the formation filter AND refetches that zone's
   * details, as two independent requests. Without a stamp the panel could pair
   * one zone's LTD with another zone's well counts — a workspace holding WCA
   * (LTD 241) and Dean showed WCA's LTD beside Dean's producing count, and only
   * a page refresh cleared it. The panel compares this against the zone it is
   * rendering and declines to show a mismatch.
   */
  formation: string | null;
}

export interface WellStimulationDetails {
  jobDate: string | null;
  proppantLbsPerFt: number | null;
  fluidGalPerFt: number | null;
  fluidBblPerFt: number | null;
  waterGalPerFt: number | null;
  waterBblPerFt: number | null;
  acidGalPerFt: number | null;
  acidBblPerFt: number | null;
  stageSpacingFt: number | null;
  proppantMeshSize: string | null;
  fluidSystem: string | null;
}

export interface TypeCurveWellRow extends Record<string, unknown> {
  // Identity
  href: string;
  uwi: string;
  name: string | null;
  leaseName: string | null;
  wellNumber: string | null;
  currentOperator: string | null;
  status: string;
  holeDirection: string | null;
  stateAbbreviation: string | null;

  // Dates
  permitDate: string | null;
  spudDate: string | null;
  completionDate: string | null;
  firstProductionDate: string | null;
  lastProductionDate: string | null;

  // Geology / Depth
  srpLandingZone: string | null;
  // Dropped from the /wells/:id and /queries/wells payloads — the backend column
  // (wells.srp_landing_zone) this was sourced from is retired; see srpLandingZone above.
  srpLandingZoneAttribute?: string | null;
  typeCurveFormation: string | null;
  trueVerticalDepthFeet: number | null;

  // Landing Analysis (from Structure Grid Collection via TC Scenario Area Config)
  landingZone: string | null;
  distanceToTopFt: number | null;
  formation: string | null;

  // Well Spacing
  closestInZoneUWI: string | null;
  closestInZoneTrueDistanceFt: number | null;
  closestInZoneHzDistanceFt: number | null;
  closestInZoneVtDistanceFt: number | null;
  closestInZoneOverlapPct: number | null;
  closestAnyZoneUWI: string | null;
  closestAnyZoneTrueDistanceFt: number | null;
  closestAnyZoneHzDistanceFt: number | null;
  closestAnyZoneVtDistanceFt: number | null;
  closestAnyZoneOverlapPct: number | null;
  secondClosestInZoneUWI: string | null;
  secondClosestInZoneTrueDistanceFt: number | null;
  secondClosestInZoneHzDistanceFt: number | null;
  secondClosestInZoneVtDistanceFt: number | null;
  secondClosestInZoneOverlapPct: number | null;
  secondClosestAnyZoneUWI: string | null;
  secondClosestAnyZoneTrueDistanceFt: number | null;
  secondClosestAnyZoneHzDistanceFt: number | null;
  secondClosestAnyZoneVtDistanceFt: number | null;
  secondClosestAnyZoneOverlapPct: number | null;
  avgInZoneTrueDistanceFt: number | null;
  avgInZoneHzDistanceFt: number | null;
  avgInZoneVtDistanceFt: number | null;
  avgAnyZoneTrueDistanceFt: number | null;
  avgAnyZoneHzDistanceFt: number | null;
  avgAnyZoneVtDistanceFt: number | null;
  parentChildStatus: string | null;
  numberOfParents: number | null;
  numberOfChildren: number | null;
  numberOfSiblings: number | null;
  measuredDepthFeet: number | null;
  perforatedLateralLengthFt: number | null;

  // Qualifier
  wellQualifier: string;

  // EUR
  oilEurBbl: number | null;
  gasEurMcf: number | null;
  waterEurBbl: number | null;
  scaledOilEurBbl: number | null;
  scaledGasEurMcf: number | null;
  scaledWaterEurBbl: number | null;

  // EUR per ft
  oilEurPerFtBblFt: number | null;
  gasEurPerFtMcfFt: number | null;
  waterEurPerFtBblFt: number | null;

  // Ratios
  gorMcfBbl: number | null;
  worBblBbl: number | null;
  cgrBblMmcf: number | null;
  wgrBblMmcf: number | null;
  watercutPct: number | null;

  // Stimulation
  latestWellStimulationDetails: WellStimulationDetails | null;
  proppantLbsPerFt: number | null;
  fluidGalPerFt: number | null;
  fluidBblPerFt: number | null;
  waterGalPerFt: number | null;
  waterBblPerFt: number | null;
  acidGalPerFt: number | null;
  acidBblPerFt: number | null;
  stageSpacingFt: number | null;
  proppantMeshSize: string | null;
  fluidSystem: string | null;

  // DCA / Source
  dcaScenarioId: number | null;
  /** Business id of the promoted DCA forecast that supplies the well's volumes. */
  businessId: string | null;
  sourceId: string | null;
  generatedOn: string | null;

  // Geo
  surfaceHole: GeoJSON.Point | null;
}

export type TcMapFilterMode =
  'type-curve-area' | 'visible-extent' | 'aoi-polygon';

// Master list of every body section either page can render. Each page derives
// its own subset below — the subset drives both the Page Settings checkboxes
// and which sections the content is allowed to render.
const ALL_PAGE_SECTIONS = [
  { key: 'typeCurveDetails', label: 'Type Curve Details' },
  { key: 'inventoryDetails', label: 'Inventory Details' },
  { key: 'summaryTable', label: 'Summary Table' },
  { key: 'map', label: 'Map' },
  { key: 'dcaParameters', label: 'DCA Parameters' },
  { key: 'charts', label: 'Charts' },
  { key: 'inventoryManagement', label: 'Inventory Management' },
  { key: 'unitsTable', label: 'Units Table' },
] as const;

export type PageSectionKey = (typeof ALL_PAGE_SECTIONS)[number]['key'];

export interface PageSection {
  key: PageSectionKey;
  label: string;
}

/** Plain Type Curve pages: no Inventory Details (its data only exists on the
 *  Inventory Workspace page). */
export const PAGE_SECTIONS: readonly PageSection[] = ALL_PAGE_SECTIONS.filter(
  (section) => section.key !== 'inventoryDetails'
);

/** Inventory Workspace page: every section, including Inventory Details and
 *  Charts. Charts / DCA Parameters / Summary Table are offered but default to
 *  unchecked here (see `useInitInventorySectionVisibility`). */
export const INVENTORY_PAGE_SECTIONS: readonly PageSection[] =
  ALL_PAGE_SECTIONS;

export type FilterCategoryType = 'picklist' | 'date-range' | 'numeric-range';

export interface FilterCategory {
  id: string;
  label: string;
  type: FilterCategoryType;
  param: string;
  accessor: (well: any) => string | number | null | undefined;
  facetKey?: string;
  minParam?: string;
  maxParam?: string;
  // Picklist rendered as single-select radios (exactly one value, no "any"
  // state). Used for booleans that must always resolve to true/false.
  singleSelect?: boolean;
}

export type ColorByTarget =
  'map' | 'probitPlot' | 'monthlyVolumes' | 'ratios' | 'crossPlot';

export interface ColorByConfig {
  ruleId: string;
  attribute: string;
  selectedValues: string[];
  valueColors: Record<string, string>;
  applyTo: Record<ColorByTarget, boolean>;
  enabled: boolean;
}

export type TcWellFilters = Record<
  string,
  string[] | string | number | undefined
>;

export interface MapBounds {
  sw_lng: number;
  sw_lat: number;
  ne_lng: number;
  ne_lat: number;
}

// ─── Gun Barrel request types ───────────────────────────────────────────────

export type GunBarrelPoiLayer =
  | { kind: 'structure_grid_formations'; collectionIds: number[] }
  | { kind: 'wells' }
  | { kind: 'pre_drill_well_survey_geometries' }
  | {
      kind: 'insights_unit_formation_slots';
      typeCurveHeaderDetailsId?: number;
    }
  | {
      // Inventory Workspace only: every configured zone's slots in one call.
      kind: 'inventory_unit_slots';
      inventoryWorkspaceId: number;
      inventoryFormationPackageIds?: number[];
      formationName?: string;
      zones?: { inventoryFormationPackageId: number }[];
    };

export interface GunBarrelCrossSectionEndPoints {
  start: { lat: number; lon: number };
  end: { lat: number; lon: number };
}

export interface GunBarrelQuery {
  subject: {
    kind: 'development_area';
    developmentAreaId: number;
    /**
     * The Type Curve version whose scenario area config colours the bands. Colour
     * only: it is NOT `typeCurveHeaderDetailsId`, which the backend also reads as
     * an as-of geometry key (it renders the unit as it stood at that version's
     * promotion time). Sending the version here recolours without moving a band.
     */
    scenarioTypeCurveHeaderDetailsId?: number;
    /** Inventory Workspace only: falls back for the inventory_unit_slots layer. */
    inventoryWorkspaceId?: number;
    crossSectionEndPoints?: GunBarrelCrossSectionEndPoints;
  };
  poiLayers: GunBarrelPoiLayer[];
  /** Legacy theoretical spacing — omitted for the Inventory Workspace, where the
   *  inventory_unit_slots layer carries the real per-zone slots. */
  targetSpacingFt?: number;
}

export interface GunBarrelRequest {
  query: GunBarrelQuery;
}

// ─── Unit Formation Slots (map geometry layer) ──────────────────────────────

export interface UnitFormationSlotResult {
  id: number;
  unitId: number;
  unitName: string;
  formationName: string;
  slotNumber: number;
  hasWell: boolean;
  valued: boolean;
  notes: string | null;
  uwi: string | null;
  wellProportionInUnit: number | null;
  wellStatus: string | null;
  forecastStatus: string | null;
  reserveCategory: string | null;
  geometrySourceType: string;
  geometry: string; // WKT
  geometryGeojson: GeoJSON.LineString;
  /** Set when the slot is a leg of a composite wellbore. */
  compositeId?: number | null;
  compositeName?: string | null;
  compositePosition?: number | null;
  compositeEntryEnd?: 'start' | 'end' | null;
}

export interface UnitFormationSlotsResponse {
  results: UnitFormationSlotResult[];
  /** Every composite referenced by a row on the page, once. */
  composites?: SlotComposite[];
  meta: {
    totalCount: number;
    page: number;
    pageSize: number;
  };
}

// ─── Type Curve Draft (editable workspace details form) ─────────────────────

export interface TypeCurveDraft {
  status: string;
  lateralLength: string;
  formation: string;
  wellSpacing: string;
  proppantIntensity: string;
  fluidIntensityGal: string;
  fluidIntensityBbl: string;
  vintage: string;
  operators: string[];
  developmentType: string[];
}

// ─── Inventory Verification ─────────────────────────────────────────────────

export interface UnitVerificationItem {
  id: number; // verification_item id → used for POST events
  status: boolean | null; // true=correct, false=incorrect, null=unverified
}

export interface UnitVerification {
  id: number;
  unitId: number;
  name: string;
  slotGeneratedAt: string | null;
  slotsGenerated: boolean;
  inventoryVerification: UnitVerificationItem;
}

export interface UnitVerificationsResponse {
  units: UnitVerification[];
}

export interface InventoryVerificationSummary {
  ltdGeneratedCount: number;
  verdictCount: number;
  incorrectCount: number;
  correctCount: number;
  totalUnits: number;
}
